// Keyboard navigation for lists of rows.
// j/k to move between rows, action keys trigger clicks on elements within the active row.
//
// Usage:
//   <div data-controller="keyboard-nav" data-keyboard-nav-active-class="table-active">
//     <tr data-keyboard-nav-target="row"> ... </tr>
//   </div>
//
// Action keys are defined via data-keyboard-nav-keys-value (JSON object mapping key → CSS selector).
// When a key is pressed and a row is active, the first matching element within that row is clicked.
// Action keys always operate on the single active row, never on checkbox-selected rows — bulk
// actions go through their own explicit UI (e.g. "Reject selected" button) to avoid accidental
// bulk mutations from a single keystroke.
//
// Example: data-keyboard-nav-keys-value='{"a":"[data-allow]","r":"[data-reject]","x":"input[type=checkbox]"}'

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["row"];
  static classes = ["active"];
  static values = { keys: { type: Object, default: {} } };

  connect() {
    this.index = -1;
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener("keydown", this.handleKeydown);
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleKeydown);
  }

  handleKeydown(event) {
    // Don't intercept when typing in inputs
    if (
      event.target.matches("input, textarea, select") ||
      event.metaKey ||
      event.ctrlKey
    )
      return;

    const visibleRows = this.rowTargets.filter(
      (row) => !row.hidden && row.offsetParent !== null,
    );
    if (visibleRows.length === 0) return;

    switch (event.key) {
      case "j":
        event.preventDefault();
        this.moveTo(visibleRows, this.index + 1);
        break;
      case "k":
        event.preventDefault();
        this.moveTo(visibleRows, this.index - 1);
        break;
      default:
        this.handleActionKey(event, visibleRows);
    }
  }

  moveTo(rows, newIndex) {
    // Clamp to valid range
    const clamped = Math.max(0, Math.min(newIndex, rows.length - 1));

    // Clear previous highlight
    if (this.index >= 0 && this.index < rows.length) {
      rows[this.index].classList.remove(...this.activeClasses);
    }

    this.index = clamped;
    const row = rows[this.index];
    row.classList.add(...this.activeClasses);
    row.scrollIntoView({ block: "nearest" });
  }

  handleActionKey(event, rows) {
    if (this.index < 0 || this.index >= rows.length) return;

    const selector = this.keysValue[event.key];
    if (!selector) return;

    const row = rows[this.index];
    const el = row.querySelector(selector);
    if (!el) return;

    event.preventDefault();
    el.click();
  }

  // When a row is removed (turbo_stream.remove), adjust index so highlight stays sensible
  rowTargetDisconnected() {
    const visibleRows = this.rowTargets.filter(
      (row) => !row.hidden && row.offsetParent !== null,
    );
    if (visibleRows.length === 0) {
      this.index = -1;
      return;
    }
    if (this.index >= visibleRows.length) {
      this.moveTo(visibleRows, visibleRows.length - 1);
    } else {
      // Re-highlight current index (the row shifted up)
      this.moveTo(visibleRows, this.index);
    }
  }
}
